import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../Modal';
import { Field, FormShell, type FieldErrors } from '../FormShell';
import { useToast } from '../Toast';
import { commitsQueryKey, createCommit } from '../../lib/workApi';
import { saveErrorState } from './formErrors';

const FORM_ID = 'commit-form';
const FIELDS = ['note'] as const;

/** "Commit board": snapshots the whole board with an optional note. Write users only. */
export function CommitDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  const commit = useMutation({
    mutationFn: (n: string | null) => createCommit({ note: n }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: commitsQueryKey });
      toast.success('Board committed');
      onClose();
    },
    onError: (err) => {
      const next = saveErrorState(err, FIELDS);
      setFieldErrors(next.fieldErrors);
      setFormError(next.formError);
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);
    const trimmed = note.trim();
    commit.mutate(trimmed === '' ? null : trimmed);
  }

  const footer = (
    <>
      <button type="button" onClick={onClose} className="px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-100">
        Cancel
      </button>
      <button type="submit" form={FORM_ID} id="commit-confirm" disabled={commit.isPending}
        className="px-3 py-1.5 rounded bg-atg-blue text-white hover:bg-atg-navy disabled:opacity-50">
        {commit.isPending ? 'Committing...' : 'Commit'}
      </button>
    </>
  );

  return (
    <Modal open onClose={onClose} title="Commit board" size="sm" footer={footer}>
      <FormShell id={FORM_ID} onSubmit={onSubmit} formError={formError}>
        <Field label="Note" name="note" id="commit-note" maxLength={200} autoFocus value={note}
          placeholder="e.g. Q1 planning review" onChange={(e) => setNote(e.target.value)} errors={fieldErrors} />
      </FormShell>
    </Modal>
  );
}
